import { GtmClient } from '@photosi/gtm-service';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { DATADOG_CUSTOM_ACTIONS } from 'src/_shared/datadog/DatadogCustomActions';
import Datadog from 'src/_shared/datadog/index.web';
import { UserInstance } from 'src/store/user/user';
import { ICartInfo, ICartItem, ICreatedCartRow } from '@photosi/expo-photosi-sdk/build/Cart';
import braze from 'src/_shared/braze';

export async function trackCartAddSuccessEvent(
  user: UserInstance,
  cartInfo: ICartInfo,
  createdCartRows: ICreatedCartRow[],
) {
  const { cartItems = [], vatIncludedFullTotal, orderId } = cartInfo;
  const totalItemsQuantity = cartItems.reduce((acc, item) => acc + item?.quantity, 0);
  const cartTrackingProducts: TrackingProducts = cartItems.map((item: ICartItem) => ({
    currency_code: item.currencyCode,
    product_category_code: item.productCategoryCode,
    product_category_name: item.productCategoryName,
    product_type: item.productType,
    quantity: item.quantity,
    vat_included_formatted_total_price: item.formattedVatIncludedTotalFullPrice,
    vat_included_total_price: item.vatIncludedTotalPrice,
    editorType: item.editorType,
    product_display_name: item.productDisplayName,
    product_name: item.productDisplayName?.toLowerCase().replace(/ /g, '_'),
  }));

  const addedCartItems = createdCartRows
    .map(cartRow => cartItems.find(cartItem => getCartItemId(cartItem) === cartRow.rowGuid))
    .filter((cartItem): cartItem is ICartItem => !!cartItem);

  if (addedCartItems.length === 0) return;

  const userId = String(user.id);
  addedCartItems.forEach(() => Datadog.logInfo(DATADOG_CUSTOM_ACTIONS.web_cart_add_success));

  // The caller navigates to the cart as soon as this resolves, which would cancel
  // the requests Braze and Amplitude start here — see braze.flushAfter for the details.
  // Amplitude batches on its own, so its events go out together instead of one round
  // trip per row. The GTM pushes are handed to flushAfter as a list rather than fired
  // in parallel: each updates a shared dataLayer.ecommerce model that the Braze tag
  // reads asynchronously, so they must be spaced for every Braze event to carry its own
  // product's data — flushAfter spaces them and then flushes them all at once.
  await Promise.all([
    braze.flushAfter(
      addedCartItems.map(cartItem => () => GtmClient.emitEvent(getAddToCartPush(cartItem, userId, orderId))),
    ),
    ...addedCartItems.map(({ currencyCode, productCode, productCategoryCode }) =>
      amplitude.trackEvent(AnalyticsEvents.cart.addSuccess, {
        userType: user.userType,
        userId,
        lastProductCategoryCode: productCategoryCode,
        lastProductCurrencyCode: currencyCode,
        lastProductPrice: vatIncludedFullTotal,
        orderId,
        totalProductsQuantity: totalItemsQuantity,
        unirepoLastProductCode: productCode,
        products: cartTrackingProducts,
      }),
    ),
  ]);
}

type TrackingProducts = {
  currency_code: string;
  product_category_code: string;
  product_category_name: string;
  product_type: string;
  quantity: number;
  vat_included_formatted_total_price: string;
  vat_included_total_price: number;
  editorType: string | null;
  product_display_name: string;
  product_name: string;
}[];

function getAddToCartPush(cartItem: ICartItem, userId: string, orderId: string) {
  const { editorType, currencyCode, productCode, productCategoryCode } = cartItem;

  return {
    event: 'addToCart',
    // GTM forwards this push to Braze as cart_add_success: the property names
    // below are read by the tag, and Braze property keys are case-sensitive.
    userphotoSi_ID: userId,
    ecommerce: {
      currencyCode: currencyCode || 'EUR',
      add: {
        products: getCartItemId(cartItem),
      },
      editor_type: editorType,
      unirepo_product_code: productCode,
      product_category_code: productCategoryCode,
      order_ID: orderId,
    },
  };
}

function getCartItemId(cartItem: ICartItem): string | undefined {
  return cartItem.id || (cartItem.rowDetails.length >= 1 ? cartItem.rowDetails[0].id : undefined);
}
